import React from "react";
import { getSmartChipId } from "../../utils/showIf.utils";
import AdvTextRenderer from "./AdvTextRenderer";
import DateRenderer from "./DateRenderer";
import DropdownRenderer from "./DropdownRenderer";
import FileUploadRenderer from "./FileUploadRenderer";
import "./GridLayoutRenderer.scss";
import HorizontalLineRenderer from "./HorizontalLineRenderer";
import InputRenderer from "./InputRenderer";
import MultiDimensionDisplayRenderer from "./MultiDimensionDisplayRenderer";
import NumberRenderer from "./NumberRenderer";
import TableRenderer from "./TableRenderer";
import TextLabelRenderer from "./TextLabelRenderer";
import UserChipRenderer from "./UserChipRenderer";

/**
 * GridLayoutRenderer Component
 * Renders a grid layout container with nested form elements
 * @param {Object} element - The grid layout element
 * @param {Object} formResponses - Form response values
 * @param {Function} isElementVisible - (elementId) => boolean from useShowIfVisibility
 * @param {Function} onFieldChange - Callback for field changes
 * @param {boolean} isInteractive - Whether form is interactive
 * @param {boolean} showResponses - Whether to show existing responses
 */
const GridLayoutRenderer = ({
  element,
  formResponses = {},
  isElementVisible = () => true,
  onFieldChange,
  isInteractive = false,
  showResponses = false,
  userProfileData,
  submitDetails,
  formData,
}) => {
  const { properties, gridElements = [] } = element;
  const { columnCount = 2, rowCount = 1, columnWidths = [50, 50], gap = 8 } = properties;

  // Ensure we have valid grid structure
  if (!gridElements || gridElements.length === 0) {
    return null;
  }

  // Render individual grid cell element
  const renderGridElement = (cellElement, rowIndex, colIndex) => {
    if (!cellElement) {
      return <div key={`${rowIndex}-${colIndex}`} className='grid-cell-empty' />;
    }
    const { properties, type, id } = cellElement;

    if (!isElementVisible(id)) {
      return <div key={`${rowIndex}-${colIndex}`} className='grid-cell-empty' />;
    }

    // Skip hidden workflow variable elements
    if (cellElement.properties?.isHidden) {
      return <div key={`${rowIndex}-${colIndex}`} className='grid-cell-empty' />;
    }

    // Skip deleted workflow variable elements
    if (cellElement?.isDeleted) {
      return <div key={`${rowIndex}-${colIndex}`} className='grid-cell-empty' />;
    }

    // Common props for interactive elements
    const commonProps = {
      keyValue: `${rowIndex}-${colIndex}-${id}`,
      element: cellElement,
      ...(isInteractive && {
        value: formResponses[id],
        onChange: (value) => onFieldChange?.(id, value, getSmartChipId(cellElement)),
        isInteractive: true,
      }),
      showResponses,
      isGridElement: true,
    };

    // Check if it's a user chip (could have type "dropdown" with chipType "user chip")
    if (properties?.isUserVariable || properties?.chipType === "user chip") {
      return <UserChipRenderer {...commonProps} userProfileData={userProfileData} submitDetails={submitDetails} />;
    }

    if (properties?.isFormDimensionDisplay) {
      return <MultiDimensionDisplayRenderer {...commonProps} formData={formData} formResponses={formResponses} />;
    }

    // Render based on element type
    switch (type) {
      case "text-label":
        return <TextLabelRenderer element={cellElement} />;
      case "dropdown":
        return <DropdownRenderer {...commonProps} />;
      case "text":
      case "input":
        return <InputRenderer {...commonProps} />;
      case "date":
        return <DateRenderer {...commonProps} />;
      case "number":
        return <NumberRenderer {...commonProps} />;
      case "file-upload":
        return (
          <FileUploadRenderer
            {...commonProps}
            element={{
              ...cellElement,
              value: isInteractive ? (formResponses[id] ?? cellElement.value) : cellElement.value,
            }}
            uploadedAttachments={formData?.attachments?.[id]}
          />
        );
      case "horizontal-line":
        return <HorizontalLineRenderer element={cellElement} />;
      case "table":
        return <TableRenderer {...commonProps} />;
      case "advanced-text":
        return <AdvTextRenderer {...commonProps} />;
      default:
        return (
          <div key={commonProps.keyValue} className='grid-cell-unknown'>
            <p>Unknown element type: {type}</p>
          </div>
        );
    }
  };

  // Generate CSS Grid template columns
  const gridTemplateColumns = columnWidths.map((width) => `${width}%`).join(" ");

  return (
    <div className='grid-layout-renderer'>
      <div
        className='grid-container'
        style={{
          display: "grid",
          gridTemplateColumns,
          gridTemplateRows: `repeat(${rowCount}, auto)`,
        }}>
        {gridElements.map((row, rowIndex) =>
          row.map((cell, colIndex) => (
            <div key={`cell-${rowIndex}-${colIndex}`} className='grid-cell align-items-center'>
              {renderGridElement(cell, rowIndex, colIndex)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GridLayoutRenderer;
